import type { SourceListBlock, SourceRef } from "../contract/types";
import { Icon } from "../components/Icon";
import type { BlockViewProps } from "./types";

/**
 * Fuentes citadas: la trazabilidad visible de la respuesta.
 *
 * Se agrupan por procedencia —lo que está en el sílabo frente a lo que viene
 * de fuera— porque la distinción es lo que el estudiante necesita para saber
 * qué le van a evaluar. `pdf` cuenta como material del curso, no como externo.
 */

const TRUST_LABEL: Record<NonNullable<SourceRef["trust"]>, string> = {
  unverified: "Sin verificar",
  "machine-confirmed": "Verificado por máquina",
  "human-reviewed": "Revisado",
};

function isOutside(source: SourceRef): boolean {
  return source.kind === "outside";
}

function SourceItem({
  source,
  onNavigate,
}: {
  source: SourceRef;
  onNavigate?: BlockViewProps["onNavigate"];
}) {
  const title = source.title ?? source.path;
  return (
    <li className="sources__item">
      <span className="sources__icon" aria-hidden="true">
        <Icon name={source.kind === "outside" ? "external" : "book"} size="0.875rem" />
      </span>
      <div className="sources__body">
        <div className="sources__head">
          {onNavigate ? (
            <button
              type="button"
              className="sources__title sources__title--link"
              onClick={() => onNavigate({ kind: "source", path: source.path })}
            >
              {title}
            </button>
          ) : (
            <span className="sources__title">{title}</span>
          )}
          {source.page !== null && source.page !== undefined ? (
            <span className="sources__page u-nums">p. {source.page}</span>
          ) : null}
        </div>

        <p className="sources__path u-mono">{source.path}</p>

        {source.snippet ? <p className="sources__snippet">{source.snippet}</p> : null}

        <div className="sources__badges">
          {source.trust ? (
            <span className={`sources__badge sources__badge--${source.trust}`}>
              <Icon name={source.trust === "unverified" ? "alert" : "check-circle"} size="0.6875rem" />
              {TRUST_LABEL[source.trust]}
            </span>
          ) : null}
          {source.stale ? (
            <span className="sources__badge sources__badge--stale">
              <Icon name="clock" size="0.6875rem" />
              Desactualizado
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export function SourceList({ block, onNavigate }: BlockViewProps<SourceListBlock>) {
  const sources = block.sources ?? [];
  const course = sources.filter((source) => !isOutside(source));
  const outside = sources.filter(isOutside);

  if (sources.length === 0) {
    return <p className="atom__empty">Sin fuentes citadas.</p>;
  }

  return (
    <div className="atom atom--sources sources">
      <SourceGroup
        title="Del curso"
        count={course.length}
        sources={course}
        onNavigate={onNavigate}
      />
      {outside.length > 0 ? (
        <SourceGroup
          title="Fuera del sílabo"
          count={outside.length}
          sources={outside}
          onNavigate={onNavigate}
          tone="outside"
        />
      ) : null}
    </div>
  );
}

function SourceGroup({
  title,
  count,
  sources,
  onNavigate,
  tone,
}: {
  title: string;
  count: number;
  sources: SourceRef[];
  onNavigate?: BlockViewProps["onNavigate"];
  tone?: "outside";
}) {
  if (count === 0) return null;
  return (
    <section className={`sources__group ${tone ? `sources__group--${tone}` : ""}`}>
      <h4 className="sources__group-title">
        {title}
        <span className="sources__count u-nums">{count}</span>
      </h4>
      <ul className="sources__list">
        {sources.map((source, index) => (
          <SourceItem key={`${source.path}-${index}`} source={source} onNavigate={onNavigate} />
        ))}
      </ul>
    </section>
  );
}

export default SourceList;
